import { HORSE_PROFILES, type EditorMode, type FieldStatus, type HorseProfile, type HorseStatus } from './mockData.js';
import type { HorseRecord } from '../_shared/useContactCollections.js';

// In-memory review state only. Production saves still require the scoped,
// revision-checked horse commands and their confirmed receipts.
export const HORSE_REVIEW_KEY = 'horse-review';
export const NEW_HORSE_ID = '__new__';

// W32 (2026-10-20): where the horses in this review state came from.
//   'fixture'  the DUKE design profile (preview / dev mock only)
//   'hubspot'  GET /v1/contacts/:id/horses (real session; may be empty)
// A real-session screen renders the horse cards only once the seed is
// 'hubspot', so the fixture never flashes while the read is in flight.
export type HorseSeed = 'fixture' | 'hubspot';
export const PREVIEW_SAVE_ACTOR = 'Jamie Dorsey · Preview';
export const REAL_SAVE_LABEL = 'Saved in this review only, not written to HubSpot';

export interface HorseEditorValues {
  name: string;
  breed: string;
  age: string;
  birth: string;
  discipline: string;
  horseStatus: HorseStatus;
  story: string;
  doseStatus: FieldStatus;
  dose: string;
  feedStatus: FieldStatus;
  feed: string;
  reasonStatus: FieldStatus;
  reason: string;
  goalStatus: FieldStatus;
  goal: string;
}

export interface HorseChange {
  id: string;
  customerId: string;
  horseId: string;
  kind: 'added' | 'profile' | 'story' | 'routine' | 'goals';
  actor: string;
  recordedAt: string;
  before: Partial<HorseEditorValues>;
  after: Partial<HorseEditorValues>;
  effectiveDate: string | null;
  note: string;
}

export interface ReviewHorse extends HorseProfile {
  customerId: string;
  revision: number;
  facts: Pick<HorseEditorValues, 'doseStatus' | 'dose' | 'feedStatus' | 'feed' | 'reasonStatus' | 'reason' | 'goalStatus' | 'goal'>;
  history: HorseChange[];
  /** W32: HubSpot horse_gender_N when the horse came from the contact record. */
  gender?: string | null;
}

export interface HorseDraft {
  customerId: string;
  revision: number;
  baseline: HorseEditorValues;
  values: HorseEditorValues;
  effectiveDate: string;
  note: string;
}

export interface HorseReviewState {
  customerId: string;
  horses: ReviewHorse[];
  selectedId: string;
  drafts: Record<string, HorseDraft>;
  nextId: number;
  /** W32: optional so states created before this field (and tests) still type-check; undefined means 'fixture'. */
  seed?: HorseSeed;
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const blankText = (value: string) => /^(not recorded|unknown)$/i.test(value.trim()) ? '' : value;

export function dateInput(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return validDate(value, false) ? value : '';
  const m = value.match(/^([A-Za-z]+) (\d{1,2}), (\d{4})$/);
  if (!m) return '';
  const month = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(m[1].slice(0, 3).toLowerCase()) + 1;
  if (!month) return '';
  const iso = `${m[3]}-${String(month).padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return validDate(iso, false) ? iso : '';
}

export function validDate(value: string, notFuture = true): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value &&
    (!notFuture || value <= new Date().toISOString().slice(0, 10));
}

function fact(value: string): { status: FieldStatus; value: string } {
  if (!value || /^not (discussed|recorded)/i.test(value)) return { status: 'Not discussed', value: '' };
  if (/^(unknown|customer does not know)$/i.test(value)) return { status: 'Customer does not know', value: '' };
  if (value === 'Customer declined') return { status: 'Customer declined', value: '' };
  return { status: 'Provided', value: value === 'Provided · details needed' ? '' : value };
}

export function reportedOrStatus(status: FieldStatus, value: string): string {
  return status === 'Provided' ? value.trim() || 'Provided · details needed' : status;
}

function factsFrom(horse: HorseProfile): ReviewHorse['facts'] {
  const dose = fact(horse.reportedProduct.dailyAmount), feed = fact(horse.reportedProduct.feedingRoutine);
  const reason = fact(horse.goals.reason), goal = fact(horse.goals.goal);
  return { doseStatus: dose.status, dose: dose.value, feedStatus: feed.status, feed: feed.value,
    reasonStatus: reason.status, reason: reason.value, goalStatus: goal.status, goal: goal.value };
}

export function createHorseReviewState(customerId: string, profiles: HorseProfile[] = HORSE_PROFILES, seed: HorseSeed = 'fixture'): HorseReviewState {
  return { customerId, horses: profiles.map(horse => ({ ...clone(horse), customerId, revision: 1, facts: factsFrom(horse), history: [] })),
    selectedId: profiles[0]?.id ?? NEW_HORSE_ID, drafts: {}, nextId: 1, seed };
}

// W32: the api-server horse body carries more than the W26 HorseRecord
// type declares (MEASURED 2026-10-20 on 572918887122: birthDate, barn,
// vet, trainer are present, null). Read them optionally.
export interface HorseRecordDetail extends HorseRecord {
  birthDate?: string | null;
  barn?: string | null;
  vet?: string | null;
  trainer?: string | null;
}

const text = (value: string | null | undefined): string => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : '');

/**
 * Map one GET /v1/contacts/:id/horses record onto the review profile
 * shape. Every field is either the HubSpot value or an explicit "Not
 * recorded" / "Not discussed"; nothing is borrowed from the fixture.
 */
export function horseProfileFromRecord(record: HorseRecordDetail, customerId: string, index: number): HorseProfile & { gender?: string | null } {
  const name = text(record.name) || `Horse ${index + 1}`;
  const product = text(record.products);
  const feeding = text(record.productsFeeding);
  const reason = text(record.reason);
  const goal = text(record.goal);
  const birth = text(record.birthDate);
  const age = text(record.age);
  const completeness = [
    ...(!birth ? [{ key: 'birth', label: 'Birth date' }] : []),
    ...(!feeding ? [{ key: 'dose', label: 'Daily product amount' }] : []),
    ...(!goal ? [{ key: 'goal', label: 'Customer’s goal' }] : []),
  ];
  return {
    id: text(record.id) || `${customerId}:horse_${record.slot ?? index + 1}`,
    name,
    breed: text(record.breed) || 'Not recorded',
    discipline: text(record.discipline) || 'Not recorded',
    reportedAge: age ? `${age} years` : 'Not recorded',
    birthDate: birth || 'Not recorded',
    // HubSpot has no ownership-status property on the horse slots; do not infer one.
    horseStatus: 'Ownership not confirmed',
    portraitUrl: '',
    storyText: '',
    storyAuthorNote: 'No story recorded yet.',
    reportedProduct: {
      name: product || 'Product not recorded',
      square: product ? product.split(/\s+/).map(w => w.charAt(0).toUpperCase()).join('').slice(0, 2) : '-',
      reportedUseLabel: product ? `Reported use for ${name}` : 'No reported product use',
      daysOfSupply: 'Not recorded',
      dailyAmount: 'Not discussed',
      feedingRoutine: feeding || 'Not discussed',
    },
    goals: { reason: reason || 'Not discussed', goal: goal || 'Not discussed' },
    completeness,
    colicCoverage: { status: 'Not recorded', detailLine: 'No Colic Coverage record on file for this horse.' },
    relationships: {
      barn: text(record.barn) || 'Not recorded',
      trainer: text(record.trainer) || 'Not recorded',
      veterinarian: text(record.vet) || 'Not recorded',
    },
    profileHistory: [],
    nextQuestion: {
      prompt: product ? `"How are you using ${product} with ${name} each day?"` : `"What is ${name} on today, and how much each day?"`,
      helper: 'Record the customer’s answer in their own words. Keep unknown and not discussed distinct.',
      editor: 'routine',
    },
    gender: text(record.gender) || null,
  };
}

/** Seed the review from the real horses read. Empty list is honest: no horse on file. */
export function createHorseReviewStateFromRecords(customerId: string, records: HorseRecordDetail[]): HorseReviewState {
  // The gender field rides through createHorseReviewState's clone of each profile.
  return createHorseReviewState(customerId, records.map((record, index) => horseProfileFromRecord(record, customerId, index)), 'hubspot');
}

export function emptyHorse(customerId: string, id = NEW_HORSE_ID): ReviewHorse {
  return { id, customerId, revision: 0, name: 'New horse', breed: 'Not recorded', discipline: 'Not recorded',
    reportedAge: 'Not recorded', birthDate: 'Not recorded', horseStatus: 'Ownership not confirmed', portraitUrl: '', storyText: '',
    storyAuthorNote: 'No story recorded.', reportedProduct: { name: 'Product not recorded', square: '-',
      reportedUseLabel: 'No reported product use', daysOfSupply: 'Not recorded', dailyAmount: 'Not discussed', feedingRoutine: 'Not discussed' },
    goals: { reason: 'Not discussed', goal: 'Not discussed' }, completeness: [],
    colicCoverage: { status: 'Not recorded', detailLine: 'Enrollment and active coverage have not been recorded.' },
    relationships: { barn: 'Not recorded', trainer: 'Not recorded', veterinarian: 'Not recorded' }, profileHistory: [],
    nextQuestion: { prompt: 'What would the customer like us to know about this horse?', helper: 'Keep unknown and unreported details distinct.', editor: 'profile' },
    facts: { doseStatus: 'Not discussed', dose: '', feedStatus: 'Not discussed', feed: '', reasonStatus: 'Not discussed', reason: '', goalStatus: 'Not discussed', goal: '' }, history: [] };
}

function editorValues(horse: ReviewHorse): HorseEditorValues {
  return { name: horse.name, breed: blankText(horse.breed), age: horse.reportedAge.replace(/\s*years?$/i, '').replace(/^Not recorded$/i, ''),
    birth: dateInput(horse.birthDate), discipline: blankText(horse.discipline), horseStatus: horse.horseStatus,
    story: horse.storyText, ...horse.facts };
}

export function openHorseDraft(state: HorseReviewState, horseId: string): HorseReviewState {
  const horse = horseId === NEW_HORSE_ID ? emptyHorse(state.customerId) : state.horses.find(h => h.id === horseId && h.customerId === state.customerId);
  if (!horse) throw Error('This horse is not attached to the current customer.');
  if (state.drafts[horseId]) return { ...state, selectedId: horseId };
  const values = editorValues(horse);
  if (horseId === NEW_HORSE_ID) values.name = '';
  return { ...state, selectedId: horseId, drafts: { ...state.drafts, [horseId]: {
    customerId: state.customerId, revision: horse.revision, baseline: clone(values), values, effectiveDate: '', note: '',
  } } };
}

export function horseDraftDirty(draft: HorseDraft, horseId: string): boolean {
  return horseId === NEW_HORSE_ID || JSON.stringify(draft.values) !== JSON.stringify(draft.baseline);
}

export function hasHorseDrafts(state: HorseReviewState): boolean {
  return Object.entries(state.drafts).some(([id, draft]) => horseDraftDirty(draft, id));
}

export function discardHorseDraft(state: HorseReviewState, horseId: string): HorseReviewState {
  const drafts = { ...state.drafts };
  delete drafts[horseId];
  return { ...state, drafts, selectedId: horseId === NEW_HORSE_ID ? state.horses[0]?.id ?? NEW_HORSE_ID : state.selectedId };
}

export const HORSE_FIELD_LABELS: Record<keyof HorseEditorValues, string> = {
  name: 'Name', breed: 'Breed', age: 'Reported age', birth: 'Birth date', discipline: 'Discipline', horseStatus: 'Horse status', story: 'Story',
  doseStatus: 'Daily amount status', dose: 'Daily amount', feedStatus: 'Feeding routine status', feed: 'Feeding routine',
  reasonStatus: 'Product reason status', reason: 'Product reason', goalStatus: 'Goal status', goal: 'Customer goal',
};

const fieldsFor: Record<EditorMode, Array<keyof HorseEditorValues>> = {
  profile: ['name', 'breed', 'age', 'birth', 'discipline', 'horseStatus', 'story'],
  add: ['name', 'breed', 'age', 'birth', 'discipline', 'horseStatus', 'story'], story: ['story'],
  routine: ['doseStatus', 'dose', 'feedStatus', 'feed'], goals: ['reasonStatus', 'reason', 'goalStatus', 'goal'],
};

export function saveHorseDraft(state: HorseReviewState, horseId: string, mode: EditorMode, recordedAt = new Date().toISOString(), actor = PREVIEW_SAVE_ACTOR): { state: HorseReviewState; horseId: string; changed: boolean } {
  const draft = state.drafts[horseId], isNew = horseId === NEW_HORSE_ID;
  const old = state.horses.find(h => h.id === horseId && h.customerId === state.customerId);
  if (!draft || draft.customerId !== state.customerId || (!isNew && (!old || old.revision !== draft.revision))) {
    throw Error('This horse changed. Keep the draft, then discard it and review the current profile before saving.');
  }
  if (isNew && mode !== 'add' && mode !== 'profile') throw Error('Save the new horse first.');
  const values = Object.fromEntries(Object.entries(draft.values).map(([key, value]) => [key, value.trim()])) as unknown as HorseEditorValues;
  if (mode === 'profile' || mode === 'add') {
    if (!values.name) throw Error('Enter the horse’s name.');
    if (values.birth && !validDate(values.birth)) throw Error('Check the birth date. Leave it blank if unknown.');
    // W32: HubSpot horse_age_N is a bracket ('13-18', '25+'), so a range is valid alongside a plain number.
    if (values.age && !/^(?:\d+(?:\.\d+)?|\d+\s*-\s*\d+|\d+\+|unknown)$/i.test(values.age)) throw Error('Enter the reported age in years (a number or a range such as 13-18), or unknown.');
  }
  const changed = fieldsFor[mode].filter(key => isNew || values[key] !== draft.baseline[key]);
  if (!changed.length) return { state, horseId, changed: false };
  const statusChanged = changed.includes('horseStatus') && (isNew ? values.horseStatus !== 'Ownership not confirmed' : values.horseStatus !== draft.baseline.horseStatus);
  if (statusChanged && !validDate(draft.effectiveDate)) throw Error('Enter the effective date for this status change.');
  const id = isNew ? `horse_preview_${state.nextId}` : horseId;
  const next = clone(old ?? emptyHorse(state.customerId, id));
  const before: Partial<HorseEditorValues> = {}, after: Partial<HorseEditorValues> = {};
  for (const key of changed) {
    Object.assign(before, { [key]: isNew ? '' : draft.baseline[key] });
    Object.assign(after, { [key]: values[key] });
    switch (key) {
      case 'name': next.name = values.name; break;
      case 'breed': next.breed = values.breed || 'Not recorded'; break;
      case 'age': next.reportedAge = values.age ? /^unknown$/i.test(values.age) ? 'Unknown' : `${values.age} years` : 'Not recorded'; break;
      case 'birth': next.birthDate = values.birth || 'Not recorded'; break;
      case 'discipline': next.discipline = values.discipline || 'Not recorded'; break;
      case 'horseStatus': next.horseStatus = values.horseStatus; break;
      case 'story': next.storyText = values.story; next.storyAuthorNote = values.story ? (actor === PREVIEW_SAVE_ACTOR ? 'Customer reported · Saved in this preview' : 'Customer reported · Saved in this review only') : 'No story recorded.'; break;
      default: Object.assign(next.facts, { [key]: values[key] });
    }
  }
  if (mode === 'routine') next.reportedProduct = { ...next.reportedProduct, dailyAmount: reportedOrStatus(next.facts.doseStatus, next.facts.dose), feedingRoutine: reportedOrStatus(next.facts.feedStatus, next.facts.feed) };
  if (mode === 'goals') next.goals = { reason: reportedOrStatus(next.facts.reasonStatus, next.facts.reason), goal: reportedOrStatus(next.facts.goalStatus, next.facts.goal) };
  next.completeness = [
    ...(!dateInput(next.birthDate) ? [{ key: 'birth', label: 'Birth date' }] : []),
    ...(next.facts.doseStatus === 'Not discussed' ? [{ key: 'dose', label: 'Daily product amount' }] : []),
    ...(next.facts.goalStatus === 'Not discussed' ? [{ key: 'goal', label: 'Customer’s goal' }] : []),
  ];
  next.revision++;
  next.history.push({ id: `horse_change_${state.nextId}`, customerId: state.customerId, horseId: id,
    kind: isNew ? 'added' : mode === 'add' ? 'profile' : mode, actor, recordedAt,
    before, after, effectiveDate: statusChanged ? draft.effectiveDate : null, note: statusChanged ? draft.note.trim() : '' });
  const remaining = clone(draft);
  for (const key of fieldsFor[mode]) { Object.assign(remaining.baseline, { [key]: values[key] }); Object.assign(remaining.values, { [key]: values[key] }); }
  remaining.revision = next.revision;
  if (mode === 'profile' || mode === 'add') { remaining.effectiveDate = ''; remaining.note = ''; }
  const drafts = { ...state.drafts };
  delete drafts[horseId];
  drafts[id] = remaining;
  return { state: { ...state, horses: isNew ? [...state.horses, next] : state.horses.map(h => h.id === id ? next : h),
    drafts, selectedId: id, nextId: state.nextId + 1 }, horseId: id, changed: true };
}
